import { useQuery } from '@tanstack/react-query';

import { caseRecordService } from '@/services/api';
import { useStore } from '@/store/index';
import { buildRoutePath } from '@/utils/routes';

interface UseLinkedCaseUrlResult {
  url: string | null;
  isLoading: boolean;
  error: Error | null;
}

/**
 * Hook that resolves a case ID to its full URL with org/program context.
 * Searches through all accessible programs to find the case.
 */
export const useLinkedCaseUrl = (caseId: string | undefined): UseLinkedCaseUrlResult => {
  const selectedOrganizationId = useStore(state => state.selectedOrganizationId);
  const getProgramsArray = useStore(state => state.getProgramsArray);
  const programs = getProgramsArray();

  const { data, isLoading, error } = useQuery({
    queryKey: ['linkedCaseUrl', caseId, selectedOrganizationId],
    queryFn: async () => {
      if (!caseId || !selectedOrganizationId) return null;

      // Try each program until we find the case
      for (const program of programs) {
        if (!program.id) continue;

        try {
          const result = await caseRecordService.findCaseRecords({
            organization_id: selectedOrganizationId,
            program_id: program.id,
            case_record_ids: [caseId],
            limit: 1,
          });

          if (result.case_records?.length) {
            const foundCase = result.case_records[0];
            return buildRoutePath(
              `/cases/${caseId}`,
              foundCase.organization_id!,
              foundCase.program_id!,
            );
          }
        } catch {
          // Continue to next program - case may not be in this program
        }
      }

      throw new Error('Case not found');
    },
    enabled: !!caseId && !!selectedOrganizationId && programs.length > 0,
    retry: false,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  return {
    url: data ?? null,
    isLoading,
    error: error as Error | null,
  };
};
